import { useState, useEffect, useRef } from "react";
import { ListPlus, Loader2, Library, ArrowLeft, Trash2, Pencil, X, Check, Lock, Users } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { PokemonCardTile } from "./PokemonCardTile";
import { makeVariant } from "../lib/variants";
import { fetchListCards, removeItemsFromList } from "../lib/api";

// Vue "Mes listes" (Phase 5) : listes personnalisées de l'utilisateur,
// indépendantes du statut possédée/recherchée — juste une sélection
// libre de cartes+variant (ex. "À vendre", "Cadeau anniversaire").
// Privée par défaut (cf. `private` sur chaque liste) : un ami ne voit une
// liste que si elle a été explicitement rendue non privée (onTogglePrivacy).
export const CustomListsView = ({
  lists, onCreateList, onRenameList, onDeleteList, onReloadLists, onTogglePrivacy,
  collection, conditions = {}, onStatusChange, onRemove, onCardClick,
}) => {
  const [activeListId, setActiveListId] = useState(null);
  const [activeListName, setActiveListName] = useState("");
  const [cards, setCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const activeList = lists.find(l => l.id === activeListId);

  // Le contenu se recharge aussi quand le NOMBRE de cartes de la liste change :
  // on peut ajouter une carte depuis la fiche ouverte par-dessus cette vue, et
  // sans cette dépendance il fallait sortir de la liste et y revenir pour la
  // voir apparaître. `item_count` vient de `lists`, rafraîchi par addToList.
  const activeCount = activeList?.item_count;
  // Le voile de chargement ne s'affiche qu'à l'ouverture d'une liste : sur un
  // simple rafraîchissement (ajout/retrait d'une carte), faire clignoter toute
  // la grille serait pire que le problème qu'on corrige.
  const loadedListId = useRef(null);

  useEffect(() => {
    if (!activeListId) return;
    if (loadedListId.current !== activeListId) setLoadingCards(true);
    loadedListId.current = activeListId;
    fetchListCards(activeListId)
      .then(data => {
        setCards(data.cards || []);
        setActiveListName(data.list?.name || "");
      })
      .catch(err => console.error("Error fetching list cards:", err))
      .finally(() => setLoadingCards(false));
  }, [activeListId, activeCount]);

  const handleCreate = async () => {
    const name = newListName.trim();
    if (!name) return;
    await onCreateList(name);
    setNewListName("");
  };

  const startRename = (list) => {
    setRenamingId(list.id);
    setRenameValue(list.name);
  };

  const confirmRename = async (listId) => {
    const name = renameValue.trim();
    if (name) await onRenameList(listId, name);
    setRenamingId(null);
  };

  const handleDelete = async (listId) => {
    await onDeleteList(listId);
    if (activeListId === listId) setActiveListId(null);
  };

  // Retire UNE occurrence (décrémente la quantité côté backend) ; la carte
  // ne disparaît de la grille que quand sa quantité tombe à 0.
  const removeCardFromList = async (card) => {
    await removeItemsFromList(activeListId, [{ card_id: card.id, variant: card.list_variant }]);
    setCards(prev => prev.flatMap(c => {
      if (!(c.id === card.id && c.list_variant === card.list_variant)) return [c];
      const qty = (c.list_quantity || 1) - 1;
      return qty > 0 ? [{ ...c, list_quantity: qty }] : [];
    }));
    onReloadLists();
  };

  const handleVariantClick = (cardId, variantType, currentStatus, shiftKey) => {
    if (shiftKey) {
      if (currentStatus === "wanted") onRemove(cardId, variantType);
      else onStatusChange(cardId, variantType, "wanted");
    } else {
      if (!currentStatus || currentStatus === "wanted") onStatusChange(cardId, variantType, "owned");
      else onRemove(cardId, variantType);
    }
  };

  // ── Contenu d'une liste ──
  if (activeListId) {
    return (
      <div>
        <Button variant="ghost" className="mb-4 text-zinc-400 hover:text-white hover:bg-zinc-800"
          onClick={() => setActiveListId(null)}>
          <ArrowLeft className="w-4 h-4 mr-2" />Mes listes
        </Button>
        <h2 className="text-2xl font-bold text-white mb-5 flex items-center gap-2">
          <ListPlus className="w-6 h-6 text-red-500" />{activeListName || activeList?.name}
          <span className="text-base font-normal text-zinc-500">({cards.length})</span>
        </h2>

        {loadingCards ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-red-500" />
          </div>
        ) : cards.length === 0 ? (
          <div className="text-center py-16 text-zinc-500">
            <Library className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Cette liste est vide. Ajoute des cartes depuis n'importe quelle vue (mode sélection ou fiche carte).</p>
          </div>
        ) : (
          <div className="card-grid">
            {cards.map(card => (
              <div key={`${card.id}::${card.list_variant}`} className="relative">
                <button
                  title="Retirer un exemplaire de la liste"
                  onClick={() => removeCardFromList(card)}
                  className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full flex items-center justify-center border-2"
                  style={{ background: "rgba(24,24,27,0.85)", borderColor: "#71717a", backdropFilter: "blur(4px)" }}
                >
                  <X className="w-3.5 h-3.5 text-white" />
                </button>
                {(card.list_quantity || 1) > 1 && (
                  <span
                    className="absolute top-2 left-2 z-10 px-1.5 py-0.5 rounded-md text-xs font-bold text-white"
                    style={{ background: "rgba(220,38,38,0.9)", backdropFilter: "blur(4px)" }}
                    title={`${card.list_quantity} exemplaires dans la liste`}
                  >
                    ×{card.list_quantity}
                  </span>
                )}
                <PokemonCardTile
                  card={card}
                  collectionMap={collection}
                  conditionMap={conditions}
                  onCardClick={onCardClick}
                  onVariantClick={handleVariantClick}
                  cardVariants={[makeVariant(card.list_variant)]}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Liste des listes ──
  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-5 flex items-center gap-2">
        <ListPlus className="w-6 h-6 text-red-500" />Mes listes
      </h2>

      <div className="flex gap-2 mb-6">
        <Input placeholder="Nom de la nouvelle liste..." value={newListName}
          onChange={(e) => setNewListName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
          className="max-w-sm" />
        <Button className="bg-red-600 hover:bg-red-500" disabled={!newListName.trim()} onClick={handleCreate}>
          Créer
        </Button>
      </div>

      {lists.length === 0 ? (
        <div className="text-center py-16 text-zinc-500">
          <Library className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Aucune liste pour l'instant. Crée-en une ci-dessus.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {lists.map(list => (
            <div key={list.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              {renamingId === list.id ? (
                <div className="flex gap-2 mb-2">
                  <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") confirmRename(list.id); }}
                    className="h-8" autoFocus />
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 h-8 px-2"
                    onClick={() => confirmRename(list.id)}>
                    <Check className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <button className="text-left w-full mb-2" onClick={() => setActiveListId(list.id)}>
                  <h3 className="text-lg font-semibold text-white truncate">{list.name}</h3>
                  <p className="text-sm text-zinc-500">{list.item_count} carte{list.item_count > 1 ? "s" : ""}</p>
                </button>
              )}
              <div className="flex gap-2 mb-2">
                <Button size="sm" variant="outline" className="border-zinc-700 text-white flex-1"
                  onClick={() => setActiveListId(list.id)}>
                  Ouvrir
                </Button>
                <Button size="sm" variant="outline" className="border-zinc-700 text-white"
                  onClick={() => startRename(list)} title="Renommer">
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" className="border-zinc-700 text-red-400 hover:text-red-300"
                  onClick={() => handleDelete(list.id)} title="Supprimer">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <button
                onClick={() => onTogglePrivacy?.(list.id, !(list.private ?? true))}
                title={(list.private ?? true)
                  ? "Privée — clique pour la rendre visible à tes amis"
                  : "Partagée avec tes amis — clique pour la rendre privée"}
                className={`w-full flex items-center justify-center gap-1.5 text-xs rounded-md py-1 border ${
                  (list.private ?? true)
                    ? "border-zinc-700 text-zinc-500 hover:text-zinc-300"
                    : "border-emerald-600 text-emerald-400 hover:text-emerald-300"}`}>
                {(list.private ?? true)
                  ? <><Lock className="w-3 h-3" />Privée</>
                  : <><Users className="w-3 h-3" />Partagée avec tes amis</>}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
